import { useMutation, useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Me, type ScopeType, type ViewAsOptions, type ViewAsSummary } from '../api.ts';
import { useMe } from '../auth.tsx';
import { Modal } from './Modal.tsx';
import { Button, ErrorBox, Field, Select } from './ui.tsx';

// Ver como (ADR 0103): o administrador master olha o sistema como outro perfil ou pessoa. Quem
// muda é a sessão no servidor (menu, telas e dados já respondem pelo perfil simulado); aqui ficam
// só a porta de entrada e saída e o aviso de que aquilo não é o sistema de sempre.

/** Entrar e sair recarregam a página: nada calculado com os acessos de antes fica à vista. */
function reloadHome() {
  window.location.assign('/');
}

/** Pode usar o Ver como: o master, ou quem já está no modo (que é sempre um master). */
export function canViewAs(me: Me | null | undefined): boolean {
  return !!me && (!!me.master?.active || !!me.viewAs);
}

/** Nomes dos perfis no idioma da tela, em uma linha. */
function roleNames(summary: ViewAsSummary, lang: 'pt-BR' | 'en'): string {
  return summary.roles.map((r) => r.name[lang]).join(', ');
}

export function ViewAsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return <ViewAsForm onClose={onClose} />;
}

function ViewAsForm({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'pt-BR';
  const current = useMe().data?.viewAs ?? null;
  const options = useQuery({
    queryKey: ['auth', 'view-as', 'options'],
    queryFn: () => api.get<ViewAsOptions>('/auth/view-as/options'),
  });
  const [personId, setPersonId] = useState(current?.person?.id ?? '');
  const [roleIds, setRoleIds] = useState<string[]>(
    current && !current.person ? current.roles.map((r) => r.id) : [],
  );
  const [scopeType, setScopeType] = useState<ScopeType>(current?.scopeType ?? 'group');
  const [legalEntityId, setLegalEntityId] = useState(current?.legalEntityId ?? '');
  const [branchId, setBranchId] = useState(current?.branchId ?? '');

  const start = useMutation({
    mutationFn: () =>
      api.post(
        '/auth/view-as',
        personId
          ? { personId }
          : {
              roleIds,
              scopeType,
              legalEntityId: scopeType === 'legal_entity' ? legalEntityId : null,
              branchId: scopeType === 'branch' ? branchId : null,
            },
      ),
    onSuccess: reloadHome,
  });
  const stop = useMutation({
    mutationFn: () => api.post('/auth/view-as/stop'),
    onSuccess: reloadHome,
  });

  const roles = useMemo(
    () =>
      [...(options.data?.roles ?? [])].sort((a, b) =>
        a.name[lang].localeCompare(b.name[lang], lang),
      ),
    [options.data, lang],
  );
  const ready =
    !!personId ||
    (roleIds.length > 0 &&
      (scopeType === 'group' ||
        (scopeType === 'legal_entity' && !!legalEntityId) ||
        (scopeType === 'branch' && !!branchId)));

  const toggleRole = (id: string) =>
    setRoleIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (ready) start.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t('viewAs.title')}
      description={t('viewAs.description')}
      onSubmit={submit}
      footer={
        <>
          {current && (
            <Button
              variant="secondary"
              className="sm:mr-auto"
              disabled={stop.isPending}
              onClick={() => stop.mutate()}
            >
              {t('viewAs.exit')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!ready || start.isPending}>
            {current ? t('viewAs.switch') : t('viewAs.enter')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {options.isError && <ErrorBox error={options.error} />}
        <Field label={t('viewAs.person')} hint={t('viewAs.personHint')}>
          <Select value={personId} onChange={(e) => setPersonId(e.target.value)}>
            <option value="">{t('viewAs.byHand')}</option>
            {(options.data?.people ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.assignmentCount > 0
                  ? `${u.displayName} (${u.email})`
                  : `${u.displayName} (${u.email}) · ${t('viewAs.noRoles')}`}
              </option>
            ))}
          </Select>
        </Field>
        {!personId && (
          <>
            <fieldset className="flex flex-col gap-1 text-sm">
              <legend className="mb-1 font-medium">{t('viewAs.roles')}</legend>
              <div className="max-h-[40vh] overflow-y-auto rounded-md border border-rule">
                {roles.map((r) => (
                  <label
                    key={r.id}
                    className="flex min-h-10 cursor-pointer items-center gap-3 border-b border-rule px-3 py-2 last:border-b-0 hover:bg-paper"
                  >
                    <input
                      type="checkbox"
                      checked={roleIds.includes(r.id)}
                      onChange={() => toggleRole(r.id)}
                      className="size-4 accent-accent"
                    />
                    <span className="flex-1 font-medium">{r.name[lang]}</span>
                    <span className="text-xs text-muted">
                      {t('viewAs.permissionCount', { count: r.permissionCount })}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <Field label={t('viewAs.scope')}>
              <Select value={scopeType} onChange={(e) => setScopeType(e.target.value as ScopeType)}>
                <option value="group">{t('viewAs.scopeGroup')}</option>
                <option value="legal_entity">{t('viewAs.scopeEntity')}</option>
                <option value="branch">{t('viewAs.scopeBranch')}</option>
              </Select>
            </Field>
            {scopeType === 'legal_entity' && (
              <Field label={t('viewAs.entity')} required>
                <Select value={legalEntityId} onChange={(e) => setLegalEntityId(e.target.value)}>
                  <option value="">{t('viewAs.choose')}</option>
                  {(options.data?.entities ?? []).map((e) => (
                    <option key={e.id} value={e.id}>
                      {`${e.code} · ${e.name}`}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {scopeType === 'branch' && (
              <Field label={t('viewAs.branch')} required>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{t('viewAs.choose')}</option>
                  {(options.data?.branches ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {`${b.code} · ${b.name}`}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </>
        )}
        <p className="text-xs text-muted">{t('viewAs.limits')}</p>
        <ErrorBox error={start.error ?? stop.error} />
      </div>
    </Modal>
  );
}

/** Botão do menu da pessoa: só para o master. */
export function ViewAsMenuButton({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const me = useMe().data;
  if (!canViewAs(me)) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-9 items-center gap-1.5 self-start text-sm text-brand-text hover:underline"
    >
      <Eye size={16} strokeWidth={1.75} aria-hidden />
      {t('viewAs.open')}
    </button>
  );
}

/** Faixa no topo de toda tela e moldura na janela, enquanto o modo vale. */
export function ViewAsNotice() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'pt-BR';
  const viewAs = useMe().data?.viewAs;
  const [open, setOpen] = useState(false);
  const stop = useMutation({
    mutationFn: () => api.post('/auth/view-as/stop'),
    onSuccess: reloadHome,
  });
  if (!viewAs) return null;
  const who = viewAs.person
    ? t('viewAs.noticePerson', { name: viewAs.person.displayName })
    : t('viewAs.noticeRoles', { roles: roleNames(viewAs, lang) || t('viewAs.noneRole') });
  const until = new Date(viewAs.until).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-40 shadow-[inset_0_0_0_3px_var(--brand)]"
      />
      <div
        role="status"
        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand bg-brand-soft px-4 py-2 text-sm text-ink"
      >
        <p>
          <span className="font-medium">{who}</span>{' '}
          <span className="text-muted">{t('viewAs.noticeHint', { time: until })}</span>
        </p>
        <span className="flex gap-2">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            {t('viewAs.change')}
          </Button>
          <Button disabled={stop.isPending} onClick={() => stop.mutate()}>
            {t('viewAs.exit')}
          </Button>
        </span>
      </div>
      <ViewAsDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
